import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/Modal';
import Modal from '../../components/Modal';
import TabBar from '../../components/TabBar';
import ForceCompleteModal from '../../components/ForceCompleteModal';
import { Users, Phone, UserPlus, CheckCircle2, XCircle, Plus, X, KeyRound, Camera, Wallet, Pencil, Hash, Trash2, Bell, Gauge } from 'lucide-react';
import api from '../../utils/api';
import { useSocket } from '../../contexts/SocketContext';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import { fmtMoney } from '../../utils/clipboard';

export default function UserManagementPage() {
  const { isAdmin, user: currentUser } = useAuth();
  const { subscribe } = useSocket();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showEarningsModal, setShowEarningsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  // 添加用户：一行一条 { nickname 昵称(选填), username 5位账号(必填,留空自动生成), phone 手机号(选填), price 统一单价(选填) }，+号新增行
  const [addRows, setAddRows] = useState([{ nickname: '', username: '', phone: '', price: '' }]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addResult, setAddResult] = useState(null);
  // 编辑资料
  const [editForm, setEditForm] = useState({ nickname: '', username: '', phone: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  // 设置价格弹窗：排五、证券两个业务各一个价格。init 记下打开时的值，只提交改动过的那一项。
  const [price, setPrice] = useState('');               // 排五：代理商 = 结算价（基准价），普通用户 = 单价
  const [puzzlePrice, setPuzzlePrice] = useState('');   // 证券：同样规则，独立于排五
  const [priceInit, setPriceInit] = useState({ legacy: '', puzzle: '' });
  const [priceSaving, setPriceSaving] = useState(false);
  const [agentBasePrice, setAgentBasePrice] = useState('');
  const [agentPuzzlePrice, setAgentPuzzlePrice] = useState('');   // 设为代理时的证券结算价

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users || []);
    } catch (err) {
      showToast('加载用户列表失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Socket 实时推送：用户变更立即刷新
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'users_updated' || event === 'stats_refresh') fetchUsers();
    });
  }, [subscribe, fetchUsers]);

  // 行操作
  const emptyRow = { nickname: '', username: '', phone: '', price: '' };
  const updateRow = (idx, field, val) =>
    setAddRows(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: val } : r)));
  const addRow = () => setAddRows(prev => [...prev, { ...emptyRow }]);
  const removeRow = (idx) =>
    setAddRows(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : [{ ...emptyRow }]));

  // 行校验统计
  const validCount = addRows.filter(r => {
    const hasAny = r.nickname.trim() || r.username.trim() || r.phone.trim();
    if (!hasAny) return false;
    if (r.username.trim() && !/^\d{5}$/.test(r.username.trim())) return false;
    if (r.phone.trim() && !/^1[3-9]\d{9}$/.test(r.phone.trim())) return false;
    return true;
  }).length;
  const hasInvalid = addRows.some(r => {
    if (!(r.nickname.trim() || r.username.trim() || r.phone.trim())) return false;
    if (r.username.trim() && !/^\d{5}$/.test(r.username.trim())) return true;
    if (r.phone.trim() && !/^1[3-9]\d{9}$/.test(r.phone.trim())) return true;
    return false;
  });
  const hasDup = addRows.map(r => r.username.trim()).filter(Boolean).some((v, i, arr) => arr.indexOf(v) !== i);

  const handleAdd = async () => {
    const rows = addRows.filter(r => r.nickname.trim() || r.username.trim() || r.phone.trim());
    if (rows.length === 0) { showToast('请至少填写一行（昵称/账号/手机号均可）', 'error'); return; }
    if (hasInvalid) { showToast('账号需为5位数字，手机号需11位、1开头', 'error'); return; }
    if (hasDup) { showToast('账号有重复，请检查', 'error'); return; }

    setAddSubmitting(true);
    setAddResult(null);
    try {
      // 单价：批次内统一（取第一行的价格应用到本批全部用户，可留空）
      const p = addRows[0]?.price !== '' && addRows[0]?.price !== undefined ? parseFloat(addRows[0].price) || 0 : null;
      const body = {
        users: rows.map(r => ({
          nickname: r.nickname.trim() || undefined,
          username: r.username.trim() || undefined,
          phone: r.phone.trim() || undefined,
          ...(p != null ? { price: p } : {}),
        })),
      };
      const res = await api.post('/users', body);
      setAddResult(res.data);
      if (res.data.success > 0) {
        showToast(`成功添加 ${res.data.success} 个用户${res.data.failed > 0 ? `，${res.data.failed} 个失败` : ''}`, 'success');
        setAddRows([{ ...emptyRow }]);
        fetchUsers(); // 立即刷新用户列表，不依赖 socket
      }
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => showToast(e, 'error'));
      }
    } catch (err) {
      showToast(err.response?.data?.error || '添加失败', 'error');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddRows([{ ...emptyRow }]);
    setAddResult(null);
  };

  // 编辑资料：昵称/账号/手机号 [需求#2] 三者均可独立留空清空，只要求至少保留一项
  const openEdit = (u) => {
    setSelectedUser(u);
    setEditForm({
      nickname: u.name || '',
      username: u.username || '',
      phone: String(u.phone || '').startsWith('ext') ? '' : (u.phone || ''),
    });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (editForm.username.trim() && !/^\d{5}$/.test(editForm.username.trim())) {
      showToast('账号需为5位数字', 'error'); return;
    }
    if (editForm.phone.trim() && !/^1[3-9]\d{9}$/.test(editForm.phone.trim())) {
      showToast('手机号格式错误（需11位、1开头）', 'error'); return;
    }
    if (!editForm.nickname.trim() && !editForm.username.trim() && !editForm.phone.trim()) {
      showToast('昵称、账号、手机号三者至少保留一项', 'error'); return;
    }
    setEditSubmitting(true);
    try {
      const res = await api.put(`/users/${selectedUser.id}/profile`, {
        nickname: editForm.nickname.trim(),
        username: editForm.username.trim(),
        phone: editForm.phone.trim(),
      });
      showToast(res.data.message || '资料已更新', 'success');
      setShowEditModal(false);
      fetchUsers(); // 立即刷新
    } catch (err) {
      showToast(err.response?.data?.error || '更新失败', 'error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    try {
      await api.put(`/users/${userId}/status`, { status: newStatus });
      showToast(newStatus === 'active' ? '已启用' : '已禁用', 'success');
      // 乐观更新本地 state，避免 fetchUsers 异步返回前按钮状态卡住（再次点击会重复禁用/启用）
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      fetchUsers(); // 后台兜底同步，确保数据最终一致
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  // 查看用户收益（管理员/代理商）：拉取汇总 + 单价 + 最近结算明细
  const handleOpenEarnings = async (u) => {
    setSelectedUser(u);
    setShowEarningsModal(true);
    setEarningsData(null);
    setEarningsLoading(true);
    try {
      const res = await api.get(`/settlement/earnings/${u.id}`);
      setEarningsData(res.data);
    } catch (err) {
      showToast(err.response?.data?.error || '加载收益失败', 'error');
      setShowEarningsModal(false);
    } finally {
      setEarningsLoading(false);
    }
  };

  // 保存价格：排五走 /users/:id/price，证券走 /puzzle/price/:id（两个业务的价格互不影响）。
  // 只提交改动过的项；两项都改时分别提交、分别报错（比如证券超过了自己的证券结算价，排五仍然保存成功）。
  const handleSetPrice = async () => {
    if (priceSaving || !selectedUser) return;
    const isAgentTarget = selectedUser.role === 'agent';
    const word = isAgentTarget ? '结算价' : '单价';
    const jobs = [];
    if (String(price).trim() !== String(priceInit.legacy).trim()) {
      jobs.push({ key: 'legacy', name: '排五', run: () => api.put(`/users/${selectedUser.id}/price`, { price: parseFloat(price) || 0 }) });
    }
    if (String(puzzlePrice).trim() !== String(priceInit.puzzle).trim()) {
      jobs.push({ key: 'puzzle', name: '证券', run: () => api.put(`/puzzle/price/${selectedUser.id}`, { price: parseFloat(puzzlePrice) || 0 }) });
    }
    if (!jobs.length) { showToast('价格没有修改', 'info'); return; }
    setPriceSaving(true);
    const okNames = [], errs = [];
    const nextInit = { ...priceInit };
    for (const j of jobs) {
      try {
        await j.run();
        okNames.push(j.name);
        nextInit[j.key] = j.key === 'legacy' ? price : puzzlePrice;
      } catch (err) {
        errs.push(`${j.name}：${err.response?.data?.error || '设置失败'}`);
      }
    }
    setPriceInit(nextInit);
    setPriceSaving(false);
    if (okNames.length) fetchUsers();   // 立即刷新
    if (!errs.length) {
      showToast(`${okNames.join('、')}${word}设置成功`, 'success');
      setShowPriceModal(false);
    } else {
      // 部分失败：弹窗保持打开，已成功的项不会再重复提交
      showToast((okNames.length ? `${okNames.join('、')}已保存；` : '') + errs.join('；'), 'error');
    }
  };

  const handleSetAgent = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/set-agent`, { basePrice: parseFloat(agentBasePrice) || 0 });
      // 证券结算价（选填）：和当前证券单价不同才提交；此时对方已是代理商，服务端按“结算价不能超过您的证券结算价”校验
      let puzzleErr = '';
      if (String(agentPuzzlePrice).trim() !== String(selectedUser.puzzlePrice > 0 ? selectedUser.puzzlePrice : '').trim()) {
        try { await api.put(`/puzzle/price/${selectedUser.id}`, { price: parseFloat(agentPuzzlePrice) || 0 }); }
        catch (err) { puzzleErr = err.response?.data?.error || '设置失败'; }
      }
      if (puzzleErr) showToast(`已设置为代理，但证券结算价没有保存：${puzzleErr}（可在“设置结算价”里重新设置）`, 'error');
      else showToast('已设置为代理', 'success');
      setShowAgentModal(false);
      fetchUsers(); // 立即刷新
    } catch (err) {
      showToast(err.response?.data?.error || '设置失败', 'error');
    }
  };

  const handleRemoveAgent = async (userId) => {
    const ok = await confirm('确认取消该用户的代理身份？', { danger: true });
    if (!ok) return;
    try {
      await api.put(`/users/${userId}/remove-agent`);
      showToast('已取消代理身份', 'success');
      fetchUsers(); // 立即刷新
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  const handleResetPassword = async (user) => {
    const label = user.name || user.username || user.phone;
    const ok = await confirm(`确认重置「${label}」的登录密码？`, { detail: '重置后该用户须用今日验证码重新登录，登录后系统会引导其设置新密码。', danger: true });
    if (!ok) return;
    try {
      const res = await api.post(`/admin/users/${user.id}/reset-password`);
      showToast(res.data.message, 'success');
      fetchUsers(); // 立即刷新
    } catch (err) {
      showToast(err.response?.data?.error || '重置失败', 'error');
    }
  };

  // 物理删除账号（管理员专属）：无条件级联清空该账号及其下级的全部数据
  // 删除前服务端自动 dump 备份到 /tmp，可回溯
  const handleDelete = async (u) => {
    const label = u.name || u.username || u.phone || `用户${u.id}`;
    const ok = await confirm(`确认删除「${label}」？`, { detail: '将级联清空该账号（及其名下账号）的接量批次、派发记录、结算、图片、通知等全部数据，删除前会自动备份到服务器，操作不可恢复！', danger: true });
    if (!ok) return;
    try {
      const res = await api.delete(`/users/${u.id}`);
      showToast(res.data.message || '已删除', 'success');
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  // 代理商删除名下用户（仅限直属普通用户，不允许删代理身份）
  const handleDeleteByAgent = async (u) => {
    const label = u.name || u.username || u.phone || `用户${u.id}`;
    const ok = await confirm(`确认删除「${label}」？`, { detail: '将删除该用户账号及其接量批次、派发记录、图片等数据，操作不可恢复。', danger: true });
    if (!ok) return;
    try {
      const res = await api.delete(`/users/${u.id}/by-agent`);
      showToast(res.data.message || '已删除', 'success');
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  const handleToggleImagePerm = async (user) => {
    const label = user.name || user.username || user.phone;
    const next = !user.can_manage_images;
    const action = next ? '开通' : '关闭';
    const ok = await confirm(`确认${action}「${label}」的图片管理权限？`, { detail: next ? '开通后该代理商可查看/导出全体用户的工作凭证图片，用于协助核对结算。' : '关闭后将无法再访问图片管理。' });
    if (!ok) return;
    try {
      const res = await api.put(`/users/${user.id}/image-permission`, { enabled: next });
      showToast(res.data.message, 'success');
      fetchUsers(); // 立即刷新
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  // 通知系统（发消息）授权：[需求#7] 原"结算派发"改名为"通知系统"；
  // 授权后代理商可对名下用户发"结算通知/系统通知"推送
  const handleTogglePushPerm = async (user) => {
    const label = user.name || user.username || user.phone;
    const next = !user.can_send_push;
    const action = next ? '开通' : '关闭';
    const ok = await confirm(`确认${action}「${label}」的通知系统权限？`, { detail: next ? '开通后该代理商可向名下用户发送结算通知/系统通知消息。' : '关闭后将无法再发送消息。' });
    if (!ok) return;
    try {
      const res = await api.put(`/users/${user.id}/push-permission`, { enabled: next });
      showToast(res.data.message, 'success');
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  // 配额豁免：管理员对单个用户开启/关闭转换率配额豁免
  const handleToggleQuotaExempt = async (user) => {
    const label = user.name || user.username || user.phone;
    const next = !user.quota_exempt;
    const action = next ? '豁免' : '恢复';
    const ok = await confirm(
      `确认对「${label}」${action}配额限制？`,
      { detail: next ? '豁免后该用户不受转换率配额限制，每日提取不设上限（单次仍不超过“单次绝对上限”），直到你手动恢复。' : '恢复后该用户将重新受转换率配额管控。' }
    );
    if (!ok) return;
    try {
      const res = await api.put(`/users/${user.id}/quota-exempt`, { exempt: next });
      showToast(res.data.message, 'success');
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.error || '操作失败', 'error');
    }
  };

  // 代完成 / 代上传：走独立组件 ForceCompleteModal（mode 区分是否触发完成任务）
  const [forceTarget, setForceTarget] = useState(null);

  const openForceFlow = (u, mode = 'complete') => {
    setForceTarget({ userId: u.id, userName: displayName(u), mode });
  };

  // 展示名：昵称优先，其次账号，再退到手机号（避免直接把手机号当展示名）
  const displayName = (u) => u.name || u.username || (String(u.phone).startsWith('ext') ? '' : u.phone) || `用户${u.id}`;

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Users size={16} /> 用户管理
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 添加用户按钮 - 始终可见 */}
        <button
          className="btn btn-primary"
          onClick={() => setShowAddModal(true)}
          style={{ width: '100%', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 44 }}
        >
          <UserPlus size={16} /> 添加用户
        </button>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : users.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-icon"><Users size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p>暂无用户，点击上方按钮添加</p>
          </div>
        ) : (() => {
          const q = searchQuery.trim().toLowerCase();
          // 搜索相关度：0=精确匹配 1=开头匹配 2=包含匹配，相同级别保持原顺序
          // 预先计算每个用户的分数并缓存，避免 sort 内反复调用（O(n log n) 次 → n 次）
          const scoreOf = (u) => {
            const fields = [u.name || '', u.username || '', u.phone || ''].map(f => f.toLowerCase());
            if (fields.some(f => f === q))           return 0; // 精确匹配
            if (fields.some(f => f.startsWith(q)))   return 1; // 前缀匹配
            if (fields.some(f => f.includes(q)))     return 2; // 包含匹配
            return 99;
          };
          const filtered = q
            ? users
                .map((u, i) => ({ u, s: scoreOf(u), i }))   // 缓存分数 + 原始下标
                .filter(({ s }) => s < 99)
                .sort((a, b) => a.s - b.s || a.i - b.i)     // 同分时按原始顺序（稳定排序）
                .map(({ u }) => u)
            : users;
          return (
            <>
              {/* 搜索框 */}
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <input
                  className="input"
                  placeholder="搜索昵称 / 账号 / 手机号"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: 34, paddingRight: searchQuery ? 32 : 12 }}
                />
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-secondary)', lineHeight: 1, fontSize: 16 }}>✕</button>
                )}
              </div>
              {/* 搜索结果提示 */}
              {q && (
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 8, paddingLeft: 2 }}>
                  {filtered.length > 0 ? `找到 ${filtered.length} 个用户` : '没有匹配的用户'}
                </div>
              )}
              {(() => {
                // 四类分组：直属代理 → 直属用户 → 次级代理 → 普通用户
                const getGroup = (u) => {
                  const isDirect = !u.owner_id || u.owner_role === 'admin';
                  if (u.role === 'agent' && isDirect)  return 0;
                  if (u.role === 'user'  && isDirect)  return 1;
                  if (u.role === 'agent' && !isDirect) return 2;
                  return 3;
                };
                const GROUP_LABELS = ['直属代理', '直属用户', '次级代理', '普通用户'];
                const GROUP_COLORS = ['var(--c-primary)', 'var(--c-success)', '#8e5cf6', 'var(--c-text-secondary)'];
                let lastGroup = -1;
                return filtered.map(u => {
                  const g = getGroup(u);
                  const showHeader = !q && g !== lastGroup && isAdmin; // 搜索或代理视角时不显示分组标题
                  lastGroup = g;
                  return (
                    <React.Fragment key={u.id}>
                      {showHeader && (
                        <div style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                          color: GROUP_COLORS[g],
                          padding: '8px 4px 4px',
                          borderBottom: `1.5px solid ${GROUP_COLORS[g]}`,
                          marginBottom: 8, marginTop: lastGroup === g ? 0 : 4,
                          opacity: 0.85,
                        }}>
                          {GROUP_LABELS[g]}
                        </div>
                      )}
                      <div className="card" style={{ padding: 14, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {displayName(u)}
                    {u.role === 'agent' && (() => {
                      // 顶层代理（上级是管理员或无上级）显示「代理」；次级代理显示「次代」
                      const isSubAgent = u.owner_role === 'agent';
                      return (
                        <span className={`badge ${isSubAgent ? 'badge-purple' : 'badge-info'}`} style={{ marginLeft: 6 }}>
                          {isSubAgent ? '次代' : '代理'}
                        </span>
                      );
                    })()}
                    {u.is_external === 1 && <span className="badge badge-warning" style={{ marginLeft: 6 }}>外部</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {u.username && (
                      <span><Hash size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />账号 {u.username}</span>
                    )}
                    {u.phone && !String(u.phone).startsWith('ext') && (
                      <span><Phone size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />{u.phone}</span>
                    )}
                    {u.owner_name && <span> · 归属: {u.owner_name}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>
                    今日接量: {u.today_dispatched || 0} 条
                    {u.today_active_batches > 0 && (
                      <span className="badge badge-warning" style={{ marginLeft: 6 }}>有进行中任务</span>
                    )}
                  </div>
                  {/* 两个业务的价格各一行：排五 / 证券（证券没设置时显示“未设置”） */}
                  {(() => {
                    const word = u.role === 'agent' ? '结算价' : '单价';
                    const legacyVal = u.role === 'agent' ? (u.basePrice != null ? u.basePrice : u.price) : u.price;
                    const row = (biz, val, unset) => (
                      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center' }}>
                        <BizBadge biz={biz} />{word}: {val != null && !unset ? `¥${val}` : '未设置'}
                      </div>
                    );
                    return (
                      <>
                        {(u.role === 'agent' || legacyVal != null) && row('legacy', legacyVal, false)}
                        {row('puzzle', u.puzzlePrice, !(u.puzzlePrice > 0))}
                      </>
                    );
                  })()}
                </div>
                <span className={`badge ${u.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                  {u.status === 'active' ? '启用' : '禁用'}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                <button className="btn btn-outline btn-sm" onClick={() => openEdit(u)}>
                  <Pencil size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />编辑资料
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => handleToggleStatus(u.id, u.status)}>
                  {u.status === 'active' ? '禁用' : '启用'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  setSelectedUser(u);
                  // 代理商：设置单价 = 设置基准价（结算价）；普通用户：设置单价。排五、证券各一个价格
                  const l = String(u.role === 'agent' ? (u.basePrice ?? '') : (u.price ?? ''));
                  const z = String(u.puzzlePrice > 0 ? u.puzzlePrice : '');
                  setPrice(l); setPuzzlePrice(z); setPriceInit({ legacy: l, puzzle: z });
                  setShowPriceModal(true);
                }}>{u.role === 'agent' ? '设置结算价' : '设置单价'}</button>
                {u.today_active_batches > 0 && (
                  <>
                    <button className="btn btn-accent btn-sm" onClick={() => openForceFlow(u)}>代完成</button>
                    <button className="btn btn-outline btn-sm" onClick={() => openForceFlow(u, 'upload')}
                      title="为该用户每个活跃批次上传工作凭证图片，不触发完成任务">代上传</button>
                  </>
                )}
                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/admin/dispatch-view/${u.id}`)}>
                  查看数据
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => handleOpenEarnings(u)}>
                  <Wallet size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />收益
                </button>
                {/* 设为代理：管理员看全部非代理用户；代理商只看自己直属名下非代理用户 */}
                {(isAdmin || (currentUser?.role === 'agent' && Number(u.owner_id) === Number(currentUser?.id))) && u.role !== 'agent' && (
                  <button className="btn btn-outline btn-sm" onClick={() => {
                    setSelectedUser(u);
                    // 回填当前单价作为初始结算价，操作者可手动修改
                    setAgentBasePrice(String(u.price ?? ''));
                    setAgentPuzzlePrice(String(u.puzzlePrice > 0 ? u.puzzlePrice : ''));
                    setShowAgentModal(true);
                  }}>设为代理</button>
                )}
                {/* 取消代理：管理员看全部代理；代理商只看自己直属名下代理 */}
                {(isAdmin || (currentUser?.role === 'agent' && Number(u.owner_id) === Number(currentUser?.id))) && u.role === 'agent' && (
                  <button className="btn btn-outline btn-sm" style={{ color: 'var(--c-danger)' }}
                    onClick={() => handleRemoveAgent(u.id)}>
                    取消代理
                  </button>
                )}
                {isAdmin && u.role === 'agent' && (
                  <button className="btn btn-outline btn-sm" style={{ color: 'var(--c-accent)' }}
                    onClick={() => handleResetPassword(u)}
                    title="重置后该代理商须用验证码重新登录并设置新密码">
                    <KeyRound size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />重置密码
                  </button>
                )}
                {isAdmin && (
                  <button className="btn btn-outline btn-sm" style={{ color: 'var(--c-danger)' }}
                    onClick={() => handleDelete(u)}
                    title="级联清空该账号全部数据后物理删除（删除前自动备份）">
                    <Trash2 size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />删除账号
                  </button>
                )}
                {!isAdmin && u.role !== 'agent' && u.owner_id === currentUser?.id && (
                  <button className="btn btn-outline btn-sm" style={{ color: 'var(--c-danger)' }}
                    onClick={() => handleDeleteByAgent(u)}
                    title="删除该用户账号及其相关数据">
                    <Trash2 size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />删除
                  </button>
                )}
                {isAdmin && u.role === 'agent' && (
                  <button className="btn btn-outline btn-sm"
                    style={{ color: u.can_manage_images ? 'var(--c-success)' : undefined }}
                    onClick={() => handleToggleImagePerm(u)}
                    title={u.can_manage_images ? '已开通图片管理，点击关闭' : '开通后可查看/导出全体用户图片'}>
                    <Camera size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />
                    {u.can_manage_images ? <><CheckCircle2 size={11} strokeWidth={2} style={{ marginRight: 2 }} />图管已开</>  : '图片管理'}
                  </button>
                )}
                {isAdmin && u.role === 'agent' && (
                  <button className="btn btn-outline btn-sm"
                    style={{ color: u.can_send_push ? 'var(--c-success)' : undefined }}
                    onClick={() => handleTogglePushPerm(u)}
                    title={u.can_send_push ? '已开通通知系统，点击关闭' : '开通后可向名下用户发送结算通知/系统通知'}>
                    <Bell size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />
                    {u.can_send_push ? <><CheckCircle2 size={11} strokeWidth={2} style={{ marginRight: 2 }} />通知已开</> : '通知系统'}
                  </button>
                )}
                {isAdmin && u.role !== 'admin' && (
                  <button className="btn btn-outline btn-sm"
                    style={{ color: u.quota_exempt ? 'var(--c-accent)' : undefined }}
                    onClick={() => handleToggleQuotaExempt(u)}
                    title={u.quota_exempt ? '该用户已豁免配额限制，点击恢复' : '豁免后该用户不受转换率配额管控'}>
                    <Gauge size={12} style={{ verticalAlign: '-1px', marginRight: 3 }} />
                    {u.quota_exempt ? <><CheckCircle2 size={11} strokeWidth={2} style={{ marginRight: 2 }} />已豁免</> : '配额豁免'}
                  </button>
                )}
              </div>
                      </div>
                    </React.Fragment>
                  );
                });
              })()}
            </>
          );
        })()
        }
      </div>

      {/* ===== 添加用户弹窗 ===== */}
      <Modal show={showAddModal} onClose={handleCloseAddModal} title="添加用户">
        {addResult ? (
          /* 添加结果展示 */
          <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              {addResult.success > 0 && (
                <div style={{ flex: 1, background: 'rgba(52,199,89,0.08)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <CheckCircle2 size={24} color="var(--c-success)" style={{ marginBottom: 6 }} />
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--c-success)' }}>{addResult.success}</div>
                  <div style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>添加成功</div>
                </div>
              )}
              {addResult.failed > 0 && (
                <div style={{ flex: 1, background: 'rgba(255,59,48,0.06)', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                  <XCircle size={24} color="var(--c-danger)" style={{ marginBottom: 6 }} />
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--c-danger)' }}>{addResult.failed}</div>
                  <div style={{ fontSize: 13, color: 'var(--c-text-secondary)' }}>添加失败</div>
                </div>
              )}
            </div>
            {addResult.results?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6 }}>成功添加的账号：</div>
                <div style={{ background: 'var(--c-surface)', borderRadius: 8, padding: 10, fontSize: 13, maxHeight: 120, overflowY: 'auto', lineHeight: 1.8 }}>
                  {addResult.results.map((r, i) => `${r.nickname || r.username}${r.username ? `（${r.username}）` : ''}`).join('、')}
                </div>
              </div>
            )}
            {addResult.errors?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-danger)', marginBottom: 6 }}>失败原因：</div>
                <div style={{ background: 'rgba(255,59,48,0.04)', borderRadius: 8, padding: 10, fontSize: 13, maxHeight: 100, overflowY: 'auto', lineHeight: 1.8 }}>
                  {addResult.errors.join('\n')}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => setAddResult(null)}>继续添加</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCloseAddModal}>完成</button>
            </div>
          </div>
        ) : (
          /* 添加表单：一行一条，昵称 + 账号 + 手机号 + 单价 */
          <div>
            <p style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 10, lineHeight: 1.6 }}>
              昵称（选填）+ 5 位数字账号（必填，留空自动生成）+ 手机号（选填）。账号为登录凭证，全局唯一；昵称/手机号后续可在列表中补全。
            </p>

            {/* 列标题 */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 600, color: 'var(--c-text-tertiary)', padding: '0 2px' }}>
              <span style={{ width: 24, flexShrink: 0 }}>#</span>
              <span style={{ flex: 1.1 }}>昵称</span>
              <span style={{ flex: 1.1 }}>账号(5位)</span>
              <span style={{ flex: 1.2 }}>手机号(选填)</span>
              <span style={{ width: 26, flexShrink: 0 }} />
            </div>

            {addRows.map((row, idx) => {
              const usernameInvalid = row.username.trim() !== '' && !/^\d{5}$/.test(row.username.trim());
              const phoneInvalid = row.phone.trim() !== '' && !/^1[3-9]\d{9}$/.test(row.phone.trim());
              const dup = row.username.trim() !== '' &&
                addRows.filter(r => r.username.trim() === row.username.trim()).length > 1;
              return (
                <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ width: 24, fontSize: 13, fontWeight: 700, color: 'var(--c-text-secondary)', flexShrink: 0, textAlign: 'center' }}>#{idx + 1}</span>
                  <input className="input" placeholder="昵称" maxLength={20}
                    value={row.nickname}
                    onChange={e => updateRow(idx, 'nickname', e.target.value)}
                    style={{ flex: 1.1, height: 42, fontSize: 13 }} />
                  <input className="input" inputMode="numeric" maxLength={5} placeholder="账号"
                    value={row.username}
                    onChange={e => updateRow(idx, 'username', e.target.value.replace(/\D/g, ''))}
                    style={{ flex: 1.1, height: 42, fontSize: 13, borderColor: (usernameInvalid || dup) ? 'var(--c-danger)' : undefined }} />
                  <input className="input" inputMode="numeric" maxLength={11} placeholder="手机号"
                    value={row.phone}
                    onChange={e => updateRow(idx, 'phone', e.target.value.replace(/\D/g, ''))}
                    style={{ flex: 1.2, height: 42, fontSize: 13, borderColor: phoneInvalid ? 'var(--c-danger)' : undefined }} />
                  <button type="button" onClick={() => removeRow(idx)} disabled={addRows.length === 1}
                    title={addRows.length === 1 ? '至少保留一行' : '删除此行'}
                    style={{ width: 26, height: 42, flexShrink: 0, background: 'none', border: 'none', padding: 0, cursor: addRows.length === 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: addRows.length === 1 ? 'var(--c-text-tertiary)' : 'var(--c-danger)' }}>
                    <X size={16} />
                  </button>
                </div>
              );
            })}

            {/* 单价行：应用到本批全部用户（可留空，之后在列表里单独设置） */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ width: 24, flexShrink: 0 }} />
              <input className="input" type="number" step="0.01" min="0" inputMode="decimal" placeholder="单价（元/条，整批）"
                value={addRows[0]?.price} onChange={e => updateRow(0, 'price', e.target.value)}
                style={{ flex: 1.1, height: 42, fontSize: 13 }} />
              <div style={{ flex: 1.2 }} />
              <div style={{ width: 26, flexShrink: 0 }} />
            </div>

            {(hasInvalid || hasDup) && (
              <p style={{ fontSize: 12, color: 'var(--c-danger)', margin: '0 0 8px' }}>
                {hasInvalid && '有账号格式有误（需5位数字）或手机号格式有误'}
                {hasInvalid && hasDup && '；'}
                {hasDup && '有账号重复'}
              </p>
            )}

            {/* +号新增一行 */}
            <button type="button" className="btn btn-outline" onClick={addRow}
              style={{ width: '100%', height: 42, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Plus size={16} /> 添加一行
            </button>

            <button
              className="btn btn-primary"
              onClick={handleAdd}
              disabled={addSubmitting || validCount === 0 || hasInvalid || hasDup}
              style={{ width: '100%', height: 48, fontSize: 15 }}
            >
              {addSubmitting ? '添加中...' : `确认添加${validCount > 0 ? ` (${validCount} 个)` : ''}`}
            </button>
          </div>
        )}
      </Modal>

      {/* ===== 编辑资料弹窗 ===== */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)}
        title={`编辑资料 - ${selectedUser?.name || selectedUser?.username || ''}`}>
        <p style={{ fontSize: 12, color: 'var(--c-text-tertiary)', marginBottom: 12, lineHeight: 1.6 }}>
          昵称、账号（5位数字，全局唯一）、手机号三者都可以留空清空，但三者至少要保留一项，否则该账号将无法被识别/登录。
        </p>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>昵称</label>
          <input className="input" maxLength={20} placeholder="昵称（展示名，留空表示清空）"
            value={editForm.nickname} onChange={e => setEditForm({ ...editForm, nickname: e.target.value })} />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>账号（5位数字）</label>
          <input className="input" inputMode="numeric" maxLength={5} placeholder="5位数字账号，留空表示清空"
            value={editForm.username} onChange={e => setEditForm({ ...editForm, username: e.target.value.replace(/\D/g, '') })} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>手机号（选填）</label>
          <input className="input" inputMode="numeric" maxLength={11} placeholder="留空表示清空"
            value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, '') })} />
        </div>
        <button className="btn btn-primary" onClick={handleEdit} disabled={editSubmitting} style={{ width: '100%' }}>
          {editSubmitting ? '保存中...' : '保存'}
        </button>
      </Modal>

      {/* ===== 设置价格弹窗：排五、证券各一个价格 ===== */}
      <Modal show={showPriceModal} onClose={() => setShowPriceModal(false)}
        title={`${selectedUser?.role === 'agent' ? '设置结算价' : '设置单价'} - ${selectedUser?.name || selectedUser?.username || selectedUser?.phone}`}>
        {(() => {
          const isAgentTarget = selectedUser?.role === 'agent';
          const word = isAgentTarget ? '结算价' : '单价';
          // 代理商操作时，上限是自己在对应业务的结算价（服务端同样会校验）：给代理商设 → 不能超过；给普通用户设 → 不能达到或超过
          const me = !isAdmin ? users.find(x => Number(x.id) === Number(currentUser?.id)) : null;
          const capHint = (cap) => (me && cap > 0
            ? `不能${isAgentTarget ? '超过' : '达到或超过'}您的结算价 ¥${cap}`
            : null);
          const field = (biz, label, unit, value, setValue, hint) => (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                <BizBadge biz={biz} />{label}{word}（{unit}）
              </label>
              <input className="input" type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
                value={value} onChange={e => setValue(e.target.value)} />
              {hint && <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4 }}>{hint}</div>}
            </div>
          );
          return (
            <>
              {field('legacy', '排五', '元/条', price, setPrice, capHint(me?.basePrice))}
              {field('puzzle', '证券', '元/单', puzzlePrice, setPuzzlePrice, capHint(me?.puzzlePrice))}
              <div style={{ fontSize: 12, color: 'var(--c-text-tertiary)', margin: '-4px 0 16px', lineHeight: 1.6 }}>
                两个业务的价格互相独立；只会保存修改过的那一项。证券{word}没设置时，该对象的证券单在结算时只显示数量、不计金额。
              </div>
              <button className="btn btn-primary" onClick={handleSetPrice} disabled={priceSaving} style={{ width: '100%' }}>
                {priceSaving ? '保存中...' : '确认设置'}
              </button>
            </>
          );
        })()}
      </Modal>

      {/* ===== 设置代理弹窗 ===== */}
      <Modal show={showAgentModal} onClose={() => setShowAgentModal(false)}
        title={`设为代理 - ${selectedUser?.name || selectedUser?.username || selectedUser?.phone}`}>
        <p style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
          设置该代理商的结算价（即上级与该代理商之间的结算单价），排五、证券各一个。代理商给下级设置的价格不能超过对应业务的此值。
        </p>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'block' }}>
            结算价（元/条）
          </label>
          <input className="input" type="number" step="0.01" min="0" inputMode="decimal" placeholder="0.00"
            value={agentBasePrice} onChange={e => setAgentBasePrice(e.target.value)} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 4, display: 'flex', alignItems: 'center' }}>
            <BizBadge biz="puzzle" />证券结算价（元/单，选填）
          </label>
          <input className="input" type="number" step="0.01" min="0" inputMode="decimal" placeholder="不填则保持当前证券单价"
            value={agentPuzzlePrice} onChange={e => setAgentPuzzlePrice(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={handleSetAgent} style={{ width: '100%' }}>确认设置</button>
      </Modal>

      {/* ===== 用户收益弹窗 ===== */}
      <Modal show={showEarningsModal} onClose={() => setShowEarningsModal(false)}
        title={`收益 - ${selectedUser?.name || selectedUser?.username || selectedUser?.phone}`}>
        {earningsLoading ? (
          <p style={{ textAlign: 'center', padding: 24, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : earningsData ? (
          <div>
            {/* 汇总 */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <div style={{ flex: 1, background: 'rgba(52,199,89,0.08)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--c-success)' }}>¥{fmtMoney(earningsData.total)}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>累计收益</div>
              </div>
              <div style={{ flex: 1, background: 'var(--c-primary-light)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--c-primary)' }}>¥{fmtMoney(earningsData.today)}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>今日收益</div>
              </div>
              <div style={{ flex: 1, background: 'var(--c-bg)', borderRadius: 12, padding: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--c-text)' }}>¥{fmtMoney(earningsData.month)}</div>
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 2 }}>本月收益</div>
              </div>
            </div>

            {/* 单价与结算次数 */}
            <div style={{ background: 'var(--c-bg)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: 'var(--c-text-secondary)', display: 'flex', justifyContent: 'space-between' }}>
              <span>单价: {earningsData.price != null ? `¥${earningsData.price}` : '未设置'}</span>
              <span>结算 {earningsData.settlementCount || 0} 次</span>
            </div>

            {/* 最近结算明细 */}
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-secondary)', marginBottom: 6 }}>
              最近结算明细
            </div>
            {earningsData.recentRecords?.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--c-text-tertiary)', padding: '16px 0', textAlign: 'center' }}>暂无结算记录</p>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto', border: '0.5px solid var(--c-border)', borderRadius: 10 }}>
                {earningsData.recentRecords.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    padding: '9px 12px', borderBottom: i < earningsData.recentRecords.length - 1 ? '0.5px solid var(--c-border)' : 'none',
                    fontSize: 13
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{r.settlement_date}</div>
                      <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 1 }}>
                        {r.count || 0} 条{r.price != null && r.price > 0 ? ` × ¥${Number(r.price).toFixed(2)}` : ''}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, color: 'var(--c-success)', flexShrink: 0 }}>¥{fmtMoney(r.total_amount)}</div>
                  </div>
                ))}
              </div>
            )}

            <button className="btn btn-outline" onClick={() => setShowEarningsModal(false)} style={{ width: '100%', marginTop: 14, height: 44 }}>
              关闭
            </button>
          </div>
        ) : null}
      </Modal>

      {/* ===== 代完成/代上传弹层：使用独立组件 ForceCompleteModal (逐批次传图，mode 决定是否调 force-complete) ===== */}
      {forceTarget && (
        <ForceCompleteModal
          userId={forceTarget.userId}
          userName={forceTarget.userName}
          mode={forceTarget.mode}
          onClose={() => setForceTarget(null)}
          onDone={() => {
            fetchUsers();
            showToast(forceTarget.mode === 'upload' ? '已代为上传凭证' : '已代为完成', 'success');
          }}
        />
      )}

      <TabBar mode="admin" />
    </div>
  );
}

// 业务标签（排五 / 证券），和结算管理页里的一致
function BizBadge({ biz }) {
  const puzzle = biz === 'puzzle';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, borderRadius: 5, padding: '1px 6px', marginRight: 6, flexShrink: 0,
      background: puzzle ? 'rgba(175,82,222,0.14)' : 'rgba(0,122,255,0.12)',
      color: puzzle ? '#8944ab' : 'var(--c-primary)',
    }}>{puzzle ? '证券' : '排五'}</span>
  );
}
